import { useSpring } from 'framer-motion'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useDataContext } from 'context/data-context'
import { WelcomeModal } from 'components/radio/welcome-modal'
import { Portal } from 'components/portal'
import { usePlayerContext } from 'context/player-context'
import { playStart } from 'utils'
import { StyledVideo, StyledPopupControl } from './styled'

type Props = {
  value: number
  videoFileUrl: string
}

const NOISE_MULTIPLIER = 0.5
const VIDEO_NOISE_URL =
  'https://docs.rferl.org/Infographics/2023/2023_01/2023_01_Anniversary/video/video_noise.mp4'

let timeoutId: ReturnType<typeof setTimeout>

export const Video: FC<Props> = ({ videoFileUrl }) => {
  const { addNotification } = useDataContext()
  const {
    permissionsGranted,
    grantPermissions,
    masterVolume,
    isMuted,
    isVideoHelpDone,
    isPopupVideoShow,
    setPopupVideoShow,
  } = usePlayerContext()
  const noiseRef = useRef<HTMLAudioElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlayingNoise, setPlayingNoise] = useState(false)
  const [currentTime, setCurrentTime] = useState<number>(0)
  const noiseVolume = useSpring(0, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  })
  const videoVolume = useSpring(0, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  })

  const noiseLevel = useMemo(() => {
    return isPlayingNoise
      ? masterVolume * NOISE_MULTIPLIER * Number(!isMuted)
      : 0
  }, [masterVolume, isPlayingNoise, isMuted])

  const videoLevel = useMemo(() => {
    return masterVolume * Number(!isMuted)
  }, [masterVolume, isMuted])

  // set volume
  useEffect(() => {
    if (noiseRef.current) {
      noiseVolume.set(noiseLevel)
    }
    if (videoRef.current) {
      videoVolume.set(videoLevel)
    }
  }, [noiseLevel, videoLevel])

  // start play noise
  useEffect(() => {
    if (noiseRef.current) {
      if (isVideoHelpDone && isPlayingNoise && !isMuted) {
        playStart(noiseRef)
      }
    }
  }, [isVideoHelpDone, isPlayingNoise, isMuted])

  // start play video
  useEffect(() => {
    if (isVideoHelpDone) {
      clearTimeout(timeoutId)
      setPlayingNoise(true)

      if (videoFileUrl && videoRef.current) {
        timeoutId = setTimeout(() => {
          playStart(videoRef, videoFileUrl, addNotification)
          setPlayingNoise(false)
        }, 200)
      }
    }

    return () => clearTimeout(timeoutId)
  }, [isVideoHelpDone, videoFileUrl])

  useEffect(() => {
    const unsubscribeVideo = videoVolume.onChange(volume => {
      if (videoRef.current) {
        videoRef.current.volume = volume
      }
    })
    const unsubscribeNoise = noiseVolume.onChange(volume => {
      if (noiseRef.current) {
        noiseRef.current.volume = volume
      }
    })

    return () => {
      unsubscribeVideo()
      unsubscribeNoise()
    }
  }, [])

  const onModalSwitchHandler = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime)
    }
    setPopupVideoShow(!isPopupVideoShow)
  }, [isPopupVideoShow])

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = currentTime
    }
  }, [isPopupVideoShow])

  return (
    <>
      <audio
        src="https://docs.rferl.org/Infographics/2023/2023_01/2023_01_Anniversary/audio/radio_noise.mp3"
        loop
        ref={noiseRef}
      />
      {isPopupVideoShow ? (
        <Portal>
          <StyledPopupControl
            isOpen={isPopupVideoShow}
            onClick={onModalSwitchHandler}
          />
          <StyledVideo
            preload="auto"
            ref={videoRef}
            key={videoFileUrl}
            autoPlay
            isOpen={isPopupVideoShow}
            controls={isPopupVideoShow}
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture={true}
          >
            <source type={videoFileUrl && 'video/mp4'} src={videoFileUrl} />
          </StyledVideo>
        </Portal>
      ) : (
        <>
          <StyledVideo
            preload="auto"
            ref={videoRef}
            key={videoFileUrl}
            loop={!videoFileUrl}
            autoPlay
            $stretch
          >
            <source
              type={videoFileUrl && 'video/mp4'}
              src={videoFileUrl || VIDEO_NOISE_URL}
            />
          </StyledVideo>
        </>
      )}
      {videoFileUrl && (
        <StyledPopupControl isOpen={false} onClick={onModalSwitchHandler} />
      )}
      <WelcomeModal isOpen={!permissionsGranted} onClose={grantPermissions} />
    </>
  )
}
